import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { moldFormSchema, type MoldFormData } from '@/schemas/moldSchemas';
import type { Mold } from '@/types/legacy';
import { Loader2, Nfc, X, Check } from 'lucide-react';
import { useNFCReader } from '@/hooks/useNFCReader';
import { useScannerContext } from '@/contexts/ScannerContext';
import { Badge } from '@/components/ui/badge';
import { toast as sonnerToast } from 'sonner';

interface MoldFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mold?: Mold | null;
}

export function MoldFormDialog({ open, onOpenChange, mold }: MoldFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const { scanTagUid, isScanning, isSupported, lastRead: mobileLastRead, writeFullTag } = useNFCReader();
  const [writeNfcOnSave, setWriteNfcOnSave] = useState(false);
  const [lastProcessedTimestamp, setLastProcessedTimestamp] = useState<number>(0);
  const { nfcBridge, writeTag: writeTagUsb } = useScannerContext();

  // NFC disponible si hay soporte móvil O lector USB conectado
  const nfcAvailable = isSupported || nfcBridge.isReaderReady;

  // Cargar lista de clientes activos para el desplegable
  const { data: customers } = useQuery({
    queryKey: ['customers-for-molds'],
    queryFn: async () => {
      const { data, error } = await api
        .from('customers')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data || []) as Array<{ id: string; name: string }>;
    },
    enabled: open,
  });

  const form = useForm<MoldFormData>({
    resolver: zodResolver(moldFormSchema),
    defaultValues: {
      name: '',
      code: '',
      type: '',
      tag: '',
      customer_id: '',
      nfc_tag_uid: '',
      current_location: '',
      shelf_location: '',
      description: '',
      material: '',
      dimensions: '',
      number_of_pieces: 0,
      max_uses: 0,
      maintenance_hours: 0,
      cycle_time_minutes: null,
      is_active: true,
      notes: '',
    },
  });

  // Detectar lectura NFC desde lector USB
  useEffect(() => {
    if (open && nfcBridge.lastRead) {
      const timeSinceRead = Date.now() - nfcBridge.lastRead.timestamp;
      if (timeSinceRead < 10000) {
        const uid = nfcBridge.lastRead.uid;
        form.setValue('nfc_tag_uid', uid);
        sonnerToast.success(`NFC detectado (USB): ${uid}`);
      }
    }
  }, [nfcBridge.lastRead, open, form]);

  // Detectar lectura NFC desde móvil
  useEffect(() => {
    if (open && mobileLastRead && mobileLastRead.timestamp > lastProcessedTimestamp) {
      const uid = mobileLastRead.serialNumber;
      form.setValue('nfc_tag_uid', uid);
      setLastProcessedTimestamp(mobileLastRead.timestamp);
      sonnerToast.success(`NFC detectado (Móvil): ${uid}`);
    }
  }, [mobileLastRead, open, form, lastProcessedTimestamp]);

  // Al abrir el dialog, verificar si hay una lectura reciente del MÓVIL
  useEffect(() => {
    const currentUid = form.getValues('nfc_tag_uid');
    if (open && mobileLastRead && !currentUid) {
      const isRecent = Date.now() - mobileLastRead.timestamp < 30000;
      if (isRecent && mobileLastRead.timestamp > lastProcessedTimestamp) {
        form.setValue('nfc_tag_uid', mobileLastRead.serialNumber);
        setLastProcessedTimestamp(mobileLastRead.timestamp);
        sonnerToast.success(`UID reciente detectado (Móvil): ${mobileLastRead.serialNumber}`);
      }
    }
  }, [open, mobileLastRead, form, lastProcessedTimestamp]);

  // Reset form when mold changes or dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        name: mold?.name || '',
        code: mold?.code || '',
        type: mold?.type || '',
        tag: mold?.tag || '',
        customer_id: (mold as any)?.customer_id || '',
        nfc_tag_uid: (mold as any)?.nfc_tag_uid || '',
        current_location: (mold as any)?.current_location || '',
        shelf_location: (mold as any)?.shelf_location || '',
        description: mold?.description || '',
        material: mold?.material || '',
        dimensions: mold?.dimensions || '',
        number_of_pieces: mold?.number_of_pieces || 0,
        max_uses: mold?.max_uses || 0,
        maintenance_hours: mold?.maintenance_hours || 0,
        cycle_time_minutes: (mold as any)?.cycle_time_minutes || null,
        is_active: mold?.is_active ?? true,
        notes: mold?.notes || '',
      });
    }
  }, [open, mold, form]);

  const handleScanNFC = async () => {
    const result = await scanTagUid();
    if (result?.serialNumber) {
      // Check if this UID is already assigned to another mold
      const { data: existingMold } = await api
        .from('molds')
        .select('id,name,code')
        .eq('nfc_tag_uid', result.serialNumber)
        .neq('id', mold?.id || '')
        .maybeSingleRow();

      if (existingMold) {
        toast({
          title: 'NFC ya asignado',
          description: `Este NFC ya está asignado al molde: ${existingMold.name} (${existingMold.code})`,
          variant: 'destructive',
        });
        return;
      }

      form.setValue('nfc_tag_uid', result.serialNumber);
      toast({
        title: 'NFC escaneado',
        description: `UID: ${result.serialNumber}`,
      });
    }
  };

  const handleClearNFC = () => {
    form.setValue('nfc_tag_uid', '');
  };

  const onSubmit = async (data: MoldFormData) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        code: data.code || null,
        type: data.type || null,
        tag: data.tag || null,
        customer_id: data.customer_id || null,
        nfc_tag_uid: data.nfc_tag_uid || null,
        current_location: data.current_location || null,
        shelf_location: data.current_location === 'Almacén' ? (data.shelf_location || null) : null,
        description: data.description || null,
        material: data.material || null,
        dimensions: data.dimensions || null,
        number_of_pieces: data.number_of_pieces || null,
        max_uses: data.max_uses || null,
        maintenance_hours: data.maintenance_hours || null,
        cycle_time_minutes: data.cycle_time_minutes || null,
        is_active: data.is_active,
        notes: data.notes || null,
      };

      let savedMoldId: string | undefined = mold?.id;
      const previousNfcUid = (mold as any)?.nfc_tag_uid || null;

      if (mold) {
        const { error } = await api
          .from('molds')
          .update(payload)
          .eq('id', mold.id);

        if (error) throw error;

        toast({
          title: 'Molde actualizado',
          description: 'El molde se actualizó correctamente',
        });
      } else {
        const { data: created, error } = await api
          .from('molds')
          .insert([payload])
          .select('id')
          .single();

        if (error) throw error;
        savedMoldId = (created as any)?.id;

        toast({
          title: 'Molde creado',
          description: 'El molde se creó correctamente',
        });
      }

      // Sincronizar nfc_tags: fuente de verdad para el lookup desde NFCCaptures
      if (savedMoldId) {
        // Si el UID anterior cambió, desvincular el anterior
        if (previousNfcUid && previousNfcUid !== data.nfc_tag_uid) {
          await api.from('nfc_tags')
            .update({ assigned_to_mold_id: null })
            .eq('tag_uid', previousNfcUid);
        }
        // Si hay UID nuevo, upsert (link/relink)
        if (data.nfc_tag_uid) {
          const { error: tagErr } = await api.from('nfc_tags').upsert({
            tag_uid: data.nfc_tag_uid,
            tag_label: data.name,
            tag_type: 'mold',
            assigned_to_mold_id: savedMoldId,
            status: 'active',
          }, { onConflict: 'tag_uid' });
          if (tagErr) {
            // No abortar la operación principal, solo avisar
            sonnerToast.warning(`NFC asignado al molde, pero falló sync con nfc_tags: ${tagErr.message}`);
          }
        }
      }

      // Escribir NFC si está activado y hay UID
      if (writeNfcOnSave && data.nfc_tag_uid) {
        const nfcPayload = {
          tagType: 'mold',
          moldId: mold?.id || 'new',
          moldName: data.name,
          moldCode: data.code,
          timestamp: Date.now(),
        };

        // Intentar USB primero, luego móvil
        if (nfcBridge.isReaderReady) {
          sonnerToast.info('Acerca el chip al lector USB para escribir...');
          const success = await writeTagUsb(nfcPayload);
          if (success) {
            sonnerToast.success('Datos escritos en el chip NFC');
          }
        } else if (isSupported) {
          await writeFullTag(data.nfc_tag_uid, nfcPayload);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['molds'] });
      onOpenChange(false);
      form.reset();
      setWriteNfcOnSave(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el molde',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mold ? 'Editar Molde' : 'Nuevo Molde'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Molde A1" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Código</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: M-001" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Molde simple" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proyecto</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: SCR Targets, Tramontana, Seat Cupra..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <Select
                      value={field.value || '__none__'}
                      onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar cliente..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {customers?.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                        <SelectItem value="__none__">Sin Cliente Asociado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="number_of_pieces"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de Piezas</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : 0)}
                        placeholder="0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripción del molde" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* NFC Tag Section */}
            <FormField
              control={form.control}
              name="nfc_tag_uid"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Nfc className="w-4 h-4" />
                    Etiqueta NFC
                  </FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="UID del chip NFC"
                        readOnly
                        className="font-mono bg-muted"
                      />
                    </FormControl>
                    {field.value ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleClearNFC}
                        title="Quitar NFC"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant={field.value ? "outline" : "default"}
                      onClick={handleScanNFC}
                      disabled={isScanning || !nfcAvailable}
                    >
                      {isScanning ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Nfc className="w-4 h-4 mr-2" />
                      )}
                      {field.value ? 'Cambiar' : 'Escanear'}
                    </Button>
                  </div>
                  {field.value && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary" className="font-mono">
                        <Check className="w-3 h-3 mr-1" />
                        {field.value}
                      </Badge>
                    </div>
                  )}
                  <FormDescription>
                    {nfcAvailable
                      ? (nfcBridge.isReaderReady ? 'Acerca el chip al lector USB o escanea desde móvil' : 'Escanea el chip NFC físico pegado al molde')
                      : 'NFC no disponible - conecta lector USB o usa dispositivo móvil'}
                  </FormDescription>
                  {field.value && nfcAvailable && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-muted/50 rounded-md">
                      <Switch
                        id="write-nfc"
                        checked={writeNfcOnSave}
                        onCheckedChange={setWriteNfcOnSave}
                      />
                      <label htmlFor="write-nfc" className="text-sm cursor-pointer">
                        Escribir datos del molde en el chip NFC al guardar
                      </label>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Current Location */}
            <FormField
              control={form.control}
              name="current_location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ubicación Actual</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar ubicación" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Almacén">Almacén</SelectItem>
                      <SelectItem value="Área Laminado">Área Laminado</SelectItem>
                      <SelectItem value="Autoclave 1">Autoclave 1</SelectItem>
                      <SelectItem value="Autoclave 2">Autoclave 2</SelectItem>
                      <SelectItem value="Desmolde">Desmolde</SelectItem>
                      <SelectItem value="Taller Corte">Taller Corte</SelectItem>
                      <SelectItem value="Mantenimiento">Mantenimiento</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Ubicación física actual del molde en planta
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Shelf Location - Only shown when location is Almacén */}
            {form.watch('current_location') === 'Almacén' && (
              <FormField
                control={form.control}
                name="shelf_location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estantería</FormLabel>
                    <div className="flex gap-2">
                      <Select
                        onValueChange={(num) => {
                          const letter = field.value?.match(/[A-E]$/)?.[0] || '';
                          field.onChange(num + letter);
                        }}
                        value={field.value?.match(/^\d+/)?.[0] || ''}
                      >
                        <FormControl>
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="N°" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Array.from({ length: 40 }, (_, i) => i + 1).map((num) => (
                            <SelectItem key={num} value={String(num)}>
                              {num}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        onValueChange={(letter) => {
                          const num = field.value?.match(/^\d+/)?.[0] || '';
                          field.onChange(num + letter);
                        }}
                        value={field.value?.match(/[A-E]$/)?.[0] || ''}
                      >
                        <FormControl>
                          <SelectTrigger className="w-20">
                            <SelectValue placeholder="Fila" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {['A', 'B', 'C', 'D', 'E'].map((letter) => (
                            <SelectItem key={letter} value={letter}>
                              {letter}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {field.value && (
                        <Badge variant="secondary" className="h-10 px-3 flex items-center text-base">
                          {field.value}
                        </Badge>
                      )}
                    </div>
                    <FormDescription>
                      Número de estantería (1-40) y fila (A-E). Ej: 30C
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="material"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Material</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Acero inoxidable" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dimensions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dimensiones</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: 100x50x30 cm" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="max_uses"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usos Máximos</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : 0)}
                        placeholder="0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maintenance_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Horas Mantenimiento</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : 0)}
                        placeholder="0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cycle_time_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tiempo Ciclo (min)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Ej: 30"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El molde está disponible para producción
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {mold ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
